import { Pin, PinComponent, PinComponentOfType, PinComponentType, TeacherPin } from '../../models';
import {
  isAnswerAreaPinComponent,
  isCategoryAreaPinComponent,
  isFillInTheBlanksAreaPinComponent,
  isInteractiveQuestionPinTextPinComponent,
} from '../assertions';
import { processPinComponentReferenceFields } from '../component-reference.processor';
import { newVector } from '../geometry';
import { orderableCompareFn } from '../order';
import { randomId } from '../random-id';

export type PinComponentSettings<T extends PinComponentType> = PinComponentOfType<T>['settings'];

export function sortPinComponents<T extends PinComponent>(components: T[]): T[] {
  return [...components].sort(orderableCompareFn);
}

export function canDeletePinComponent(pin: Pin, component: PinComponent): boolean {
  return (
    !isInteractiveQuestionPinTextPinComponent(pin, component) &&
    !isAnswerAreaPinComponent(component) &&
    !isCategoryAreaPinComponent(component) &&
    !isFillInTheBlanksAreaPinComponent(component)
  );
}

export const initialPinComponentPosition = newVector(25, 25);

export function clonePinComponents(
  pinComponents: TeacherPin['pinComponents'],
  targetPin?: Pin
): {
  pinComponents: TeacherPin['pinComponents'];
  oldToNewPinComponentIds: Record<string, string>;
} {
  const oldToNewPinComponentIds: Record<string, string> = {};
  const mappedPinComponents = pinComponents.map((component) => {
    const newComponentId = randomId();
    oldToNewPinComponentIds[component.id] = newComponentId;

    return {
      ...component,
      id: newComponentId,
    };
  });

  mappedPinComponents.forEach((component, index) => {
    processPinComponentReferenceFields(component, ({ componentId }) => {
      return handleNewLinkedPinComponent(
        componentId,
        getOtherPinComponents(index, pinComponents, targetPin),
        oldToNewPinComponentIds
      );
    });
  });

  return { pinComponents: mappedPinComponents, oldToNewPinComponentIds };
}

export function getOtherPinComponents(ignoredIndex: number, pinComponents: PinComponent[], pin?: Pin): PinComponent[] {
  return [...(pin?.pinComponents || []), ...pinComponents.filter((_, filterIndex) => filterIndex !== ignoredIndex)];
}

function handleNewLinkedPinComponent(
  originalLinkedPinComponent: string,
  otherPinComponents: PinComponent[],
  clonedIdsMap: Record<string, string>
): string | undefined {
  if (!originalLinkedPinComponent) return undefined;

  if (clonedIdsMap[originalLinkedPinComponent]) {
    return clonedIdsMap[originalLinkedPinComponent];
  }

  if (otherPinComponents.map((pinComponent) => pinComponent.id).includes(originalLinkedPinComponent)) {
    return originalLinkedPinComponent;
  }

  return undefined;
}
